<template>
    <div class="container">
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h1 class="text-8 text-black mb-4">
                        Take the First Step Towards Homeownership with Magetsi Mortgage Calculator
                    </h1>
                    <p class="text-3">
                        Buying a home is one of the most exciting milestones in life—and one of the biggest financial decisions you'll ever make. At Magetsi, we understand how daunting the process can feel, especially when trying to calculate your finances. That’s why we’ve developed an intuitive mortgage calculator tool to help you simplify this journey. Whether you’re purchasing your first home, upgrading to your dream house, or exploring real estate investments in Zimbabwe, the mortgage calculator gives you the financial clarity you need to make informed decisions.
                    </p>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h2 class="text-7 text-black mb-4">
                        What Is a Mortgage Calculator, and Why Do You Need One?
                    </h2>
                    <p class="text-3">
                        A mortgage calculator is a powerful online tool that helps you estimate your monthly loan repayments based on the amount borrowed, interest rate, and loan term. For Zimbabweans navigating the complexities of property ownership, this tool provides a quick, reliable way to evaluate whether a mortgage fits your budget. Instead of relying on complicated manual calculations, you can simply input your details and get instant insights into the financial obligations you are about to enter into.
                    </p>
                    <p class="text-3">
                        But why is this tool so essential? Imagine walking into a bank or meeting a lender without fully understanding what you’re committing to. The figures they present might sound manageable on the surface, but unexpected costs or fluctuating interest rates can leave you financially stretched. By using the Magetsi mortgage calculator , you’re equipping yourself with the knowledge to ask the right questions and negotiate better terms.
                    </p>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h2 class="text-7 text-black mb-4">
                        How the Magetsi Mortgage Calculator Works
                    </h2>
                    <p class="text-3">
                        Using our mortgage calculator is as simple as it gets. In just a few steps, you’ll have a clear understanding of your estimated monthly repayments:
                    </p>
                    <ol class="text-3">
                        <li class="py-1"><strong>Enter the Loan Amount:</strong> This is the total amount you plan to borrow from your lender.</li>
                        <li class="py-1"><strong>Input the Interest Rate:</strong> Enter the rate your lender is offering.</li>
                        <li class="py-1"><strong>Choose the Loan Tenure:</strong> This is the duration of your loan, typically ranging from 5 to 30 years.</li>
                        <li class="py-1"><strong>Hit Calculate:</strong> See your estimated monthly repayments and the total cost of the loan.</li>
                    </ol>
                    <p class="text-3">This process takes less than a minute but provides invaluable insights that can guide your financial planning. </p>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Benefits of Using the Magetsi Mortgage Calculator
                    </h3>
                    <ul class="text-3">
                        <li class="py-1"><strong>Empowers You to Budget Effectively:</strong> By knowing your potential monthly repayments, you can assess whether a mortgage fits into your current financial situation. You’ll also get a clearer picture of how much to allocate for other expenses like utilities, maintenance, and insurance. </li>
                        <li class="py-1"><strong>Eliminates Surprises:</strong> One of the biggest challenges for first-time homebuyers is underestimating the true cost of a mortgage. Our calculator breaks down these costs, helping you anticipate what you’ll be paying each month and over the lifetime of the loan. </li>
                        <li class="py-1"><strong>Makes Comparison Shopping Easier:</strong> If you’re weighing offers from different lenders, the mortgage calculator allows you to input various rates and terms to see which one offers the best value. This ensures you’re making a decision based on facts, not assumptions.</li>
                        <li class="py-1"><strong>Saves Time and Effort:</strong> Instead of spending hours manually crunching numbers, you can use our tool to generate accurate results instantly. This means more time to focus on finding the perfect property or negotiating with lenders. </li>
                    </ul>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Real-Life Example: How the Mortgage Calculator Zimbabwe Can Help You
                    </h3>
                    <p class="text-3">
                        Let’s say you’re eyeing a $50,000 property in Harare. You plan to make a 20% down payment, leaving $40,000 to finance through a mortgage. If your lender offers an interest rate of 12% over a 20-year term, here’s how the Magetsi mortgage calculator can break it down for you:
                    </p>
                    <ul class="text-3">
                        <li class="py-1"><b>Monthly Repayments:</b> $440</li>
                        <li class="py-1"><b>Total Loan Cost:</b> $105,600</li>
                    </ul>
                    <p class="text-3">
                        With these figures, you’ll know exactly how much to budget each month and the overall cost of your mortgage. This transparency helps you determine whether the property is within your reach or if you need to explore other options.
                    </p>
                    <div class="text-center mt-4">
                        <button
                            @click="scrollToTop"
                            class="btn btn-primary text-4"
                        >
                            Calculate My Mortgage
                        </button>
                    </div>
                </div>
            </div>
        </div>

        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black mb-4">
                        Tips for Maximizing the Benefits of the Mortgage Calculator
                    </h3>
                    <ol class="text-3">
                        <li class="py-1">
                            <b>Be Realistic:</b> When entering your income, expenses, and property price, ensure that the values you provide reflect your actual financial situation. Being honest with your data helps you make better-informed decisions and avoids overestimating what you can afford.
                        </li>
                        <li class="py-1">
                            <b>Experiment with Different Scenarios:</b> Use the calculator to explore various interest rates, loan terms, and down payment amounts. This can help you understand how different factors influence your monthly payments and total loan cost, allowing you to find the best financing option.
                        </li>
                        <li class="py-1">
                            <b>Plan for Additional Costs:</b> Remember that a mortgage isn’t the only expense associated with buying a property. Include costs like property taxes, insurance, maintenance, and potential HOA fees in your overall budget to ensure you have a complete financial picture.
                        </li>
                        <li class="py-1">
                            <b>Consult Professionals:</b> While the mortgage calculator provides useful estimates, consulting a financial advisor or mortgage broker can help you validate your results and address any complex aspects of your mortgage journey.
                        </li>
                    </ol>
                </div>
            </div>
        </div>
        <div class="row">
            <div class="px-2 py-3 text-justify">
                <div class="my-auto mx-2">
                    <h3 class="text-6 text-black ">
                        Why Magetsi Is Your Trusted Partner
                    </h3>
                    <p class="text-3">
                        At Magetsi, we’re not just about tools and calculations—we’re about empowering you to make life-changing decisions with confidence. Our commitment to bringing digital change to Zimbabwe extends beyond ZESA tokens and bill payments. With our mortgage calculator, we’re providing a tool that simplifies one of life’s most significant investments.
                    </p>
                    <button
                        class="btn btn-primary mt-3"
                        @click="scrollToTop"
                    >
                        Get Started
                    </button>
                </div>
            </div>
        </div>

    </div>
</template>

<script>
export default {
    methods: {
        scrollToTop() {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    }
}
</script>

<style scoped>
/* Add any specific styles here */
</style>

